import { axiosInstance } from "@/api/api-client";
import { storeMedia } from "@/hooks/media-api";
import { useDeviceType } from "@/hooks/use-device-type";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import type { MediaAsset, MediaInput } from "@/types/media";
import { Camera, CheckCircle2, ImagePlus, Loader2, UploadCloud, X } from "lucide-react";
import React, { useRef, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Progress } from "../ui/progress";

const MAX_BYTES = 25 * 1024 * 1024; // 25 MB

// ─── Upload utility (same S3 flow as useMediaLibrary) ────────────────────────

async function uploadFileToMedia(
  file: File,
  onProgress: (pct: number) => void,
  signal: AbortSignal
): Promise<MediaAsset> {
  const presignRes = await axiosInstance.get(
    `/media/s3/presign?filename=${encodeURIComponent(file.name)}&contentType=${encodeURIComponent(file.type)}`
  );
  const { url, key, publicUrl } = presignRes.data as {
    url: string;
    key: string;
    publicUrl: string;
  };

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", file.type);
    const onAbort = () => {
      xhr.abort();
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal.addEventListener("abort", onAbort, { once: true });
    xhr.upload.onprogress = (evt) => {
      if (evt.lengthComputable) onProgress((evt.loaded / evt.total) * 100);
    };
    xhr.onload = () => {
      signal.removeEventListener("abort", onAbort);
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 responded ${xhr.status}: ${xhr.statusText}`));
    };
    xhr.onerror = () => {
      signal.removeEventListener("abort", onAbort);
      reject(new Error("Network error during upload"));
    };
    xhr.send(file);
  });

  const payload: MediaInput = {
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
    s3Key: key,
    s3Url: publicUrl,
    parentId: null,
  };
  const res = await storeMedia(payload);
  if (!res.success) throw new Error(res.message || "Failed to register media asset");
  return res.data as MediaAsset;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ImageUrlInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> {
  value?: string;
  onChange?: (url: string) => void;
  /** Called with the full MediaAsset after a successful file upload */
  onUploaded?: (asset: MediaAsset) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ImageUrlInput({
  value = "",
  onChange,
  placeholder = "https://example.com/image.jpg",
  disabled,
  className,
  onUploaded,
  ...rest
}: ImageUrlInputProps) {
  const { toast } = useToast();
  const { isMobile } = useDeviceType();

  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [justUploaded, setJustUploaded] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── shared upload handler ─────────────────────────────────────────────────

  const handleFile = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid file type",
        description: "Only image files are supported.",
        variant: "destructive",
      });
      return;
    }
    if (file.size > MAX_BYTES) {
      toast({
        title: "File too large",
        description: `${file.name} exceeds the 25 MB limit.`,
        variant: "destructive",
      });
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setUploading(true);
    setProgress(0);
    setJustUploaded(false);
    setPreviewError(false);

    try {
      const asset = await uploadFileToMedia(file, setProgress, controller.signal);
      const url = (asset as any).s3Url || (asset as any).fileUrl || "";
      onChange?.(url);
      onUploaded?.(asset);
      setJustUploaded(true);
      setTimeout(() => setJustUploaded(false), 3000);
      toast({ title: "Uploaded ✓", description: `${file.name} is ready.` });
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        toast({
          title: "Upload failed",
          description: err?.message || "Could not upload the image.",
          variant: "destructive",
        });
      }
    } finally {
      setUploading(false);
      setProgress(0);
      abortRef.current = null;
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleCancel = () => {
    abortRef.current?.abort();
  };

  // ── drag handlers (desktop only) ─────────────────────────────────────────

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !uploading) setDragOver(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (disabled || uploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  // ── clipboard paste ───────────────────────────────────────────────────────

  const onPaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData?.items ?? []);
    const imgItem = items.find((i) => i.kind === "file" && i.type.startsWith("image/"));
    if (imgItem) {
      e.preventDefault();
      const file = imgItem.getAsFile();
      if (file) handleFile(file);
    }
  };

  // ── file input (mobile tap) ───────────────────────────────────────────────

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const hasPreview = !!value && !previewError;

  // ══════════════════════════════════════════════════════════════════════════
  // MOBILE LAYOUT
  // ══════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <div className={cn("flex flex-col gap-2 w-full", className)}>
        {/* Hidden native file input — gives camera / gallery sheet */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          disabled={disabled || uploading}
          onChange={onFileInputChange}
        />

        {/* ── Image preview card ── */}
        {hasPreview && (
          <div className="relative rounded-xl overflow-hidden border border-slate-200 shadow-sm bg-slate-50 aspect-video w-full">
            <img
              src={value}
              alt="Image preview"
              className="w-full h-full object-contain"
              onError={() => setPreviewError(true)}
            />
            {/* Clear button */}
            {!uploading && (
              <button
                type="button"
                onClick={() => { onChange?.(""); setPreviewError(false); }}
                disabled={disabled}
                className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                title="Remove image"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            {/* Success badge */}
            {justUploaded && (
              <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-emerald-500 text-white text-xs font-semibold px-2 py-1 rounded-full shadow">
                <CheckCircle2 className="h-3 w-3" />
                Uploaded
              </div>
            )}
          </div>
        )}

        {/* ── Upload progress bar ── */}
        {uploading && (
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Uploading…</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-blue-600 tabular-nums">
                  {Math.round(progress)}%
                </span>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="h-6 w-6 rounded-full bg-blue-200 flex items-center justify-center text-blue-700 hover:bg-blue-300 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            </div>
            <Progress value={progress} className="h-2 bg-blue-200" />
          </div>
        )}

        {/* ── Tap-to-upload zone (shown when no preview) ── */}
        {!hasPreview && !uploading && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => fileInputRef.current?.click()}
            className={cn(
              "w-full rounded-xl border-2 border-dashed py-6 flex flex-col items-center gap-2 transition-all duration-200 active:scale-[0.98]",
              disabled
                ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
                : "border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50/50 active:border-blue-500"
            )}
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center">
                <Camera className="h-5 w-5 text-slate-500" />
              </div>
              <div className="flex items-center gap-2">
                <div className="h-10 w-10 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center">
                  <ImagePlus className="h-5 w-5 text-slate-500" />
                </div>
              </div>
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-slate-700">Tap to upload an image</p>
              <p className="text-xs text-slate-400 mt-0.5">Camera or photo library · Max 25 MB</p>
            </div>
          </button>
        )}

        {/* ── URL text field (collapsed helper on mobile) ── */}
        <details className="group">
          <summary className="cursor-pointer list-none flex items-center gap-1.5 text-xs text-slate-500 select-none">
            <span className="group-open:rotate-90 transition-transform inline-block">▶</span>
            Or paste a URL instead
          </summary>
          <div className="mt-2">
            <Input
              {...rest}
              value={value}
              placeholder="https://example.com/image.jpg"
              disabled={disabled || uploading}
              onChange={(e) => { onChange?.(e.target.value); setPreviewError(false); }}
              onPaste={onPaste}
              className="text-sm"
            />
          </div>
        </details>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // DESKTOP LAYOUT
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div
      className={cn("relative flex items-center w-full", className)}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* Hidden file input for desktop too (fallback) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        disabled={disabled || uploading}
        onChange={onFileInputChange}
      />

      {/* Drag-over overlay */}
      {dragOver && (
        <div className="absolute inset-0 rounded-md border-2 border-dashed border-blue-400 bg-blue-50/70 z-10 pointer-events-none flex items-center justify-center gap-2">
          <UploadCloud className="h-4 w-4 text-blue-500 animate-bounce" />
          <span className="text-xs font-semibold text-blue-600">Drop to upload</span>
        </div>
      )}

      <div className="relative flex-1">
        <Input
          {...rest}
          value={value}
          placeholder={placeholder}
          disabled={disabled || uploading}
          onChange={(e) => { onChange?.(e.target.value); setPreviewError(false); }}
          onPaste={onPaste}
          className={cn(
            "pr-20 transition-all duration-150",
            dragOver && "border-blue-400 ring-2 ring-blue-200",
            uploading && "opacity-60",
            justUploaded && "border-emerald-400 ring-2 ring-emerald-100"
          )}
        />

        {/* Right-side slot inside the input */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
          {uploading ? (
            <>
              <span className="text-[10px] font-semibold text-blue-500 tabular-nums">
                {Math.round(progress)}%
              </span>
              <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
              <button
                type="button"
                onClick={handleCancel}
                className="h-5 w-5 rounded-full bg-slate-200 flex items-center justify-center hover:bg-slate-300 transition-colors"
                title="Cancel upload"
              >
                <X className="h-3 w-3 text-slate-600" />
              </button>
            </>
          ) : justUploaded ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          ) : (
            /* Clickable upload button inside the input */
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-6 w-6 hover:bg-slate-100 rounded"
              title="Click to upload an image, or drag & drop one here"
              disabled={disabled}
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadCloud className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 transition-colors" />
            </Button>
          )}
        </div>

        {/* Slim upload progress bar under the input */}
        {uploading && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 overflow-hidden rounded-b-md">
            <div
              className="h-full bg-blue-500 transition-all duration-200"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
